/**
 * The column values the Declare-as-JSON dialog previews, so the user can see
 * "this really is JSON" before declaring:
 *
 *  - the caller's loaded rows when they carry the column — Table Explorer's
 *    grid page, the Visual Query Editor's last results;
 *  - otherwise, and only while the dialog is open, a small sample: the JSON
 *    peek's own scan (`buildJsonScanSql` — the non-NULL values of the one
 *    column, no count, scoped to the caller's other filters like the rows it
 *    stands in for, written with the table's column types so a date filter
 *    takes the engine's literal — Oracle `TO_DATE`, SQL Server's seconds —
 *    as in the grid and the peek), capped at `DECLARE_JSON_SAMPLE_LIMIT` rows.
 *    Cached per column + filters, so reopening the dialog does not read again.
 */

import { useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import type { DatabaseEngineId } from '../../../../../core/engines/engineTypes';
import { createExecutionId } from '../../../../../types/queryExecution';
import { executeQueryV2 } from '../../../../database-objects/services/queryExecutor';
import type { FilterGroup } from '../../../core/types/workspaceTypes';
import { hostCacheIdentity } from '../../../table-explorer/components/tableExplorerHostPorts';
import { fetchFilterColumnTypeOf } from '../../../table-explorer/hooks/tableMetadataQuery';
import { buildJsonScanSql } from '../../utils/jsonScanSql';

/** Rows the on-demand sample reads — enough to infer a shape, never a scan. */
export const DECLARE_JSON_SAMPLE_LIMIT = 20;

/**
 * Every row's `columnName` value (case-insensitive fallback for engines that
 * return uppercase keys when the metadata is lower), or `null` when no row
 * carries the column at all — so "not loaded" is never mistaken for "loaded,
 * and none of it is JSON".
 */
export function extractColumnValues(
  rows: ReadonlyArray<Record<string, unknown>> | undefined,
  columnName: string,
): unknown[] | null {
  if (!rows?.length || !columnName) return null;
  const lowered = columnName.toLowerCase();
  const out: unknown[] = [];
  let carried = false;
  let resolvedKey: string | undefined;
  for (const row of rows) {
    if (columnName in row) {
      carried = true;
      out.push(row[columnName]);
      continue;
    }
    if (resolvedKey === undefined) {
      resolvedKey = Object.keys(row).find((k) => k.toLowerCase() === lowered) ?? '';
    }
    if (resolvedKey && resolvedKey in row) carried = true;
    out.push(resolvedKey ? row[resolvedKey] : undefined);
  }
  return carried ? out : null;
}

export type DeclareJsonSampleArgs = {
  readonly rows: ReadonlyArray<Record<string, unknown>> | undefined;
  readonly column: string | undefined;
  readonly connectionId: string | undefined;
  readonly database: string | undefined;
  readonly schema: string | undefined;
  readonly table: string | undefined;
  readonly engine: DatabaseEngineId | undefined;
  /** The caller's other filters — the on-demand sample reads rows matching them. */
  readonly filters: FilterGroup[] | undefined;
  /** The dialog is open — the only time the on-demand sample is read. */
  readonly open: boolean;
};

export function useDeclareJsonSample({
  rows,
  column,
  connectionId,
  database,
  schema,
  table,
  engine,
  filters,
  open,
}: DeclareJsonSampleArgs): unknown[] | undefined {
  // Projected only while the dialog is open — a closed chip never walks the grid page.
  const loaded = useMemo(() => (open && column ? extractColumnValues(rows, column) : null), [open, rows, column]);
  const queryClient = useQueryClient();
  const { data } = useQuery({
    // The filters as a BigInt-safe identity: React Query hashes the key with
    // JSON.stringify even while disabled, and a host codec's chip (an Azure
    // Table Int64) holds a `bigint`.
    queryKey: ['declare-json-sample', connectionId, database, schema, table, column, hostCacheIdentity(filters ?? [])],
    queryFn: async (): Promise<unknown[]> => {
      const scope = { connectionId: connectionId!, database: database!, schema: schema!, tableName: table! };
      const sql = buildJsonScanSql({
        engine: engine!,
        schema: schema!,
        table: table!,
        column: column!,
        declaredOnText: false,
        limit: DECLARE_JSON_SAMPLE_LIMIT,
        scanFilters: filters,
        scanColumnTypeOf: await fetchFilterColumnTypeOf(queryClient, scope, filters),
      });
      const result = await executeQueryV2({
        connectionId: connectionId!,
        database: database!,
        sql,
        executionId: createExecutionId(`declare-json-sample-${column}`),
      });
      const rs = result.success ? result.resultSets?.[0] : undefined;
      if (!rs) throw new Error(result.error?.message ?? 'Could not read a sample');
      const key = rs.columns[0]?.id ?? rs.columns[0]?.name ?? column!;
      return rs.rows.map((row) => row[key]);
    },
    enabled:
      open && loaded === null && !!connectionId && !!database && schema != null && !!table && !!column && !!engine,
    staleTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
  });
  return loaded ?? data;
}
